import { useState, useEffect } from "react";
import { Btn } from "./components.js";
import { getInvoiceTitle, getTaxIdLabel, fmtCurrency, PAGE_SIZES } from "./utils.js";
import {
  getInvoice,
  getInvoicePayments,
  getTotalPaid,
  getPaymentStatus,
  deleteInvoice,
  canEditInvoice,
  transferPayments
} from "./db/index.js";

// ══════════════════════════════════════════════════════════════════════════════
// INVOICE PREVIEW COMPONENT (SUPABASE INTEGRATED - PRODUCTION READY)
// ══════════════════════════════════════════════════════════════════════════════

export default function InvoicePreview({ 
  G, 
  invoiceId,      // For PAID users (Professional/Exclusive) - fetch from DB
  invoiceData,    // For FREE users - passed from Billing.js
  user,           // User object with tier info and features
  onEditInvoice, 
  onNewInvoice, 
  onDeleteInvoice 
}) {
  // ════════════════════════════════════════════════════════════════════════════
  // STATE
  // ════════════════════════════════════════════════════════════════════════════
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [paymentTransferModal, setPaymentTransferModal] = useState(null);
  const [activeTab, setActiveTab] = useState('invoice'); // 'invoice' or 'receipt'

  // ════════════════════════════════════════════════════════════════════════════
  // FEATURE ACCESS FLAGS
  // ════════════════════════════════════════════════════════════════════════════
  
  const isFREE = user.tierLevel === 0;
  
  // Feature availability (based on tier levels from featureList.js)
  const hasEditFeature = user.tierLevel >= 2;      // Exclusive only (INVOICE_EDITING)
  const hasDeleteFeature = user.tierLevel >= 1;    // Professional+ (CLOUD_DATABASE)
  const hasEmailFeature = user.tierLevel >= 2;     // Exclusive only (future feature)
  const hasWhatsAppFeature = user.tierLevel >= 2;  // Exclusive only (future feature)
  const hasReceiptFeature = user.tierLevel >= 1;   // Professional+ (PAYMENT_TRACKING)

  // ════════════════════════════════════════════════════════════════════════════
  // DATA LOADING (TIER-BASED)
  // ════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const loadInvoice = async () => {
      setLoading(true);
      
      try {
        if (isFREE) {
          // ══════════════════════════════════════════════════════════════════
          // FREE TIER - Use passed invoiceData (no database access)
          // ══════════════════════════════════════════════════════════════════
          
          if (!invoiceData) {
            throw new Error("FREE tier requires invoiceData prop");
          }
          
          // No payment tracking for FREE tier
          setPayments([]);
          setPaymentSummary(null);
          
          // Use data directly (already has brandSnapshot/settingsSnapshot)
          // Calculate totalItems and totalQty (not stored in FREE tier)
          const processedData = {
            ...invoiceData,
            totalItems: invoiceData.lines?.length || 0,
            totalQty: invoiceData.lines?.reduce((sum, line) => sum + (line.qty || 0), 0) || 0
          };
          
          setData(processedData);
          
        } else {
          // ══════════════════════════════════════════════════════════════════
          // PAID TIER - Fetch from database
          // ══════════════════════════════════════════════════════════════════
          
          if (!invoiceId) {
            throw new Error("PAID tier requires invoiceId prop");
          }
          
          // Fetch invoice (has embedded brandSnapshot/settingsSnapshot in JSONB)
          const invoice = await getInvoice(invoiceId);
          if (!invoice) {
            throw new Error("Invoice not found");
          }
          
          // Fetch payment data
          const invoicePayments = await getInvoicePayments(invoiceId);
          const totalPaid = await getTotalPaid(invoiceId);
          const paymentStatus = await getPaymentStatus(invoiceId);
          
          setPayments(invoicePayments);
          setPaymentSummary({
            totalPaid,
            outstanding: paymentStatus.outstanding,
            status: paymentStatus.status
          });
          
          // Assemble data (match App.js loadFullInvoiceForDisplay structure)
          const assembledData = {
            ...invoice,
            
            // Map embedded snapshots to expected keys
            seller: invoice.brandSnapshot,
            formatSettings: invoice.settingsSnapshot,
            
            // Map settings snapshot fields to root level (for compatibility with existing UI code)
            currencySymbol: invoice.settingsSnapshot?.currencySymbol || "",
            currencyCode: invoice.settingsSnapshot?.currency || "NONE",
            currencyFormat: invoice.settingsSnapshot?.currencyFormat || "symbol-before-space",
            amountGrouping: invoice.settingsSnapshot?.amountGrouping || "international",
            amountSeparator: invoice.settingsSnapshot?.amountSeparator || "comma-dot",
            amountDecimals: invoice.settingsSnapshot?.amountDecimals !== false,
            
            // Map brand snapshot fields
            taxSystem: invoice.brandSnapshot?.taxSystem || "NONE",
            canadaMode: invoice.brandSnapshot?.canadaMode,
            customTaxes: invoice.brandSnapshot?.customTaxes || [],
            taxLabel: "Tax", // Default label
            
            // Terms/notes
            terms: invoice.settingsSnapshot?.notesSections || [],
            
            // Calculate totals from lines (not stored separately in new schema)
            totalItems: invoice.lines?.length || 0,
            totalQty: invoice.lines?.reduce((sum, line) => sum + (line.qty || 0), 0) || 0
          };
          
          setData(assembledData);
        }
        
      } catch (error) {
        console.error("Failed to load invoice:", error);
        alert("Failed to load invoice. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadInvoice();
  }, [invoiceId, invoiceData, isFREE]);

  // ════════════════════════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ════════════════════════════════════════════════════════════════════════════

  const handleDeleteClick = () => {
    if (!hasDeleteFeature) {
      alert(
        `Feature Not Available\n\n` +
        `Invoice deletion requires Professional tier or higher.\n\n` +
        `Upgrade to manage your invoice history.`
      );
      return;
    }
    setDeleteModal(true);
  };
  
  const handleConfirmDelete = async () => {
    try {
      await deleteInvoice(data.id);
      setDeleteModal(false);
      onDeleteInvoice(data);
    } catch (error) {
      console.error("Failed to delete invoice:", error);
      alert("Failed to delete invoice. Please try again.");
    }
  };

  const handleEditClick = async () => {
    if (!hasEditFeature) {
      alert(
        `Feature Not Available\n\n` +
        `Invoice editing requires Exclusive tier.\n\n` +
        `Upgrade to edit invoices with full payment transfer support.`
      );
      return;
    }
    
    try {
      const editCheck = await canEditInvoice(data.id);
      
      if (editCheck.hasPayments) {
        setPaymentTransferModal({
          totalPaid: editCheck.totalPaid,
          paymentCount: editCheck.paymentCount,
          payments: editCheck.payments
        });
      } else {
        onEditInvoice(data, null);
      }
    } catch (error) {
      console.error("Failed to check invoice edit status:", error);
      onEditInvoice(data, null); // Proceed anyway on error
    }
  };

  const handlePaymentTransferChoice = async (action) => {
    try {
      if (action === "transfer") {
        // Will be handled after new invoice is created
        onEditInvoice(data, { action: "transfer", fromInvoiceId: data.id });
      } else if (action === "refund") {
        // Mark payments as refunded
        await transferPayments(data.id, null, true);
        onEditInvoice(data, { action: "refund", fromInvoiceId: data.id });
      }
      setPaymentTransferModal(null);
    } catch (error) {
      console.error("Payment transfer failed:", error);
      alert("Failed to process payment transfer. Please try again.");
    }
  };

  const handleEmailClick = () => {
    if (!hasEmailFeature) {
      alert(
        `Feature Not Available\n\n` +
        `Email invoices feature requires Exclusive tier.\n\n` +
        `Upgrade to email invoices directly to customers.`
      );
      return;
    }
    
    const to = data.customer.email || "";
    const sub = `Invoice ${data.invNo} from ${data.seller?.name || "Us"}`;
    const body = `Dear ${data.customer.name},\n\nInvoice ${data.invNo} for ${fmtAmt(data.grandTotal)} is ready.\n\nThank you!\n${data.seller?.name || ""}`;
    window.open(`mailto:${to}?subject=${encodeURIComponent(sub)}&body=${encodeURIComponent(body)}`);
  };

  const handleWhatsAppClick = () => {
    if (!hasWhatsAppFeature) {
      alert(
        `Feature Not Available\n\n` +
        `WhatsApp invoices feature requires Exclusive tier.\n\n` +
        `Upgrade to send invoices via WhatsApp.`
      );
      return;
    }
    
    const ph = data.customer.phone ? data.customer.phone.replace(/\D/g, "") : ""; 
    const msg = `Hello ${data.customer.name}, invoice *${data.invNo}* for *${fmtAmt(data.grandTotal)}* from *${data.seller?.name || "us"}* is ready!`; 
    window.open((ph ? `https://wa.me/${ph}` : `https://wa.me/`) + `?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // ════════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ════════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        minHeight: "60vh",
        flexDirection: "column",
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⏳</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: G.ink }}>Loading invoice...</div>
        <div style={{ fontSize: 13, color: G.muted }}>
          {isFREE ? "Preparing invoice preview" : "Fetching invoice data from database"}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // ERROR STATE
  // ════════════════════════════════════════════════════════════════════════════

  if (!data) {
    return (
      <div style={{ 
        textAlign: "center", 
        padding: "60px 20px",
        color: G.muted
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Invoice not found</div>
        <div style={{ fontSize: 13, marginBottom: 24 }}>The invoice you're looking for doesn't exist</div>
        <Btn onClick={onNewInvoice} style={{ background: G.accent, color: G.accentFg }}>
          ← Back to Invoices
        </Btn>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // INVOICE DATA PROCESSING
  // ════════════════════════════════════════════════════════════════════════════

  const isGSTIndia  = data.taxSystem === "GST_INDIA";
  const isCanada    = data.taxSystem === "CANADA";
  const isCustom    = data.taxSystem === "CUSTOM";
  
  const showSGSTCGST = isGSTIndia && data.txType === "intra";
  const showIGST     = isGSTIndia && data.txType === "inter";
  const showHSN      = data.formatSettings?.showHSN || false;
  const showPID      = data.formatSettings?.showProductID || false;
  const showDiscount = data.formatSettings?.showDiscount !== false;
  const showCF1      = data.formatSettings?.customField1?.enabled && data.formatSettings?.customField1?.showInInvoice;
  const showCF2      = data.formatSettings?.customField2?.enabled && data.formatSettings?.customField2?.showInInvoice;
  
  const sym = data.currencySymbol !== undefined ? data.currencySymbol : "";
  const code = data.currencyCode || "NONE";
  const format = data.currencyFormat || "symbol-before-space";
  const fmtAmt = (n) => fmtCurrency(
    n,
    sym,
    format,
    code,
    data.amountGrouping || "international",
    data.amountSeparator || "comma-dot",
    data.amountDecimals !== false
  );
  const invTitle = getInvoiceTitle(data.taxSystem);

  const canadaLabels = {
    HST: ["HST"], GST_PST: ["GST", "PST"], GST: ["GST"], GST_QST: ["GST", "QST"]
  };
  const canadaTaxCols = canadaLabels[data.canadaMode] || ["GST"];

  const pageSizeKey = data.formatSettings?.pageSize || "A4";
  const pageSize = PAGE_SIZES[pageSizeKey] || PAGE_SIZES.A4;
  const scale = pageSize.scale;

  const pageStyleVars = {
    '--fs-xs': `${8.5 * scale}px`,
    '--fs-xxs': `${10 * scale}px`,
    '--fs-sm': `${10.5 * scale}px`,
    '--fs-sm2': `${11.5 * scale}px`,
    '--fs-md': `${12 * scale}px`,
    '--fs-md2': `${12.5 * scale}px`,
    '--fs-lg': `${15 * scale}px`,
    '--fs-xl': `${19 * scale}px`,
    '--fs-2xl': `${26 * scale}px`,
    width: `${pageSize.width}px`,
    minHeight: `${pageSize.height}px`,
    padding: pageSize.padding,
  };
  const printCss = `
    @media print {
      @page {
        size: ${pageSizeKey === "A4" ? "A4" : pageSizeKey === "LETTER" ? "Letter" : pageSizeKey === "LEGAL" ? "Legal" : pageSizeKey === "A5" ? "A5" : "5.5in 8.5in"};
        margin: 0;
      }
      .invoice-page, .receipt-page {
        width: 100% !important;
        min-height: auto !important; 
        overflow: visible !important; 
        box-shadow: none !important;
        margin: 0 !important;
        border-radius: 0 !important;
      }
      .watermark-container {
        position: fixed;
      }
      .no-print {
        display: none !important;
      }
      thead { display: table-header-group; } 
      tfoot { display: table-footer-group; }
    }
  `;
  // ════════════════════════════════════════════════════════════════════════════
  // BUTTON STYLING HELPER
  // ════════════════════════════════════════════════════════════════════════════
  
  const getButtonStyle = (baseStyle, isEnabled) => {
    if (isEnabled) {
      return baseStyle;
    }
    return {
      ...baseStyle,
      filter: 'grayscale(100%)',
      opacity: 0.5,
      cursor: 'not-allowed'
    };
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div>
      <style>{printCss}</style>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "24px",
            maxWidth: 460, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Delete Invoice?
            </div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 16, lineHeight: 1.5 }}>
              Delete invoice <strong>{data.invNo}</strong>?
              <br /><br />
              It will be hidden from history and reports but kept in records for audit purposes.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteModal(false)}
                style={{
                  padding: "8px 16px", border: `1px solid ${G.border}`, borderRadius: 7,
                  background: G.surface2, color: G.ink, cursor: "pointer",
                  fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 500
                }}
              >Cancel</button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: "8px 16px", border: "none", borderRadius: 7,
                  background: G.danger, color: "#FFFFFF", cursor: "pointer",
                  fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 500
                }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Transfer Modal */}
      {paymentTransferModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "24px",
            maxWidth: 520, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: G.ink }}>
              ⚠️ This invoice has received payments
            </div>
            <div style={{ 
              background: G.accentBg, 
              border: `1px solid ${G.accent}40`,
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16
            }}>
              <div style={{ fontSize: 13, color: G.ink, marginBottom: 4 }}>
                <strong>{paymentTransferModal.paymentCount}</strong> payment{paymentTransferModal.paymentCount !== 1 ? "s" : ""} totaling <strong>{fmtAmt(paymentTransferModal.totalPaid)}</strong>
              </div>
              <div style={{ fontSize: 12, color: G.muted }}>
                What should happen to these payments?
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              {/* Transfer Option */}
              <button
                onClick={() => handlePaymentTransferChoice("transfer")}
                style={{
                  background: G.surface,
                  border: `2px solid ${G.accent}`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "'Sora', sans-serif",
                  transition: "all 0.15s"
                }}
                onMouseEnter={e => e.target.style.background = G.accentBg}
                onMouseLeave={e => e.target.style.background = G.surface}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
                  ✓ Transfer to new invoice (Recommended)
                </div>
                <div style={{ fontSize: 12, color: G.muted, lineHeight: 1.4 }}>
                  Payment history preserved. New invoice will show {fmtAmt(paymentTransferModal.totalPaid)} already received.
                </div>
              </button>

              {/* Refund Option */}
              <button
                onClick={() => handlePaymentTransferChoice("refund")}
                style={{
                  background: G.surface,
                  border: `2px solid ${G.border}`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "'Sora', sans-serif",
                  transition: "all 0.15s"
                }}
                onMouseEnter={e => {
                  e.target.style.background = "#FEF2F2";
                  e.target.style.borderColor = "#EF4444";
                }}
                onMouseLeave={e => {
                  e.target.style.background = G.surface;
                  e.target.style.borderColor = G.border;
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: G.ink, marginBottom: 6 }}>
                  ↩ Mark as refunded
                </div>
                <div style={{ fontSize: 12, color: G.muted, lineHeight: 1.4 }}>
                  Customer was refunded {fmtAmt(paymentTransferModal.totalPaid)}. New invoice starts fresh with full amount due.
                </div>
              </button>
            </div>

            <button
              onClick={() => setPaymentTransferModal(null)}
              style={{
                width: "100%",
                padding: "10px",
                border: `1px solid ${G.border}`,
                borderRadius: 8,
                background: G.surface2,
                color: G.ink,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
                    </div>
        </div>
      )}

      {/* Document Tabs - Only show for PAID tier */}
      {!isFREE && (
        <div className="no-print" style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 24px",
          marginTop: payments.length > 0 ? 0 : 24
        }}>
          <div style={{
            display: "flex",
            gap: 8,
            borderBottom: `2px solid ${G.border}`,
            marginBottom: 0
          }}>
            {/* Invoice Tab */}
            <button
              onClick={() => setActiveTab('invoice')}
              style={{
                padding: "12px 24px",
                border: "none",
                borderBottom: `3px solid ${activeTab === 'invoice' ? G.accent : 'transparent'}`,
                background: activeTab === 'invoice' ? G.accentBg : 'transparent',
                color: activeTab === 'invoice' ? G.accent : G.ink,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                transition: "all 0.2s",
                marginBottom: -2
              }}
            >
              📄 Invoice
            </button>

            {/* Receipt Tab */}
            <button
              onClick={hasReceiptFeature ? () => setActiveTab('receipt') : undefined}
              disabled={!hasReceiptFeature}
              style={{
                padding: "12px 24px",
                border: "none",
                borderBottom: `3px solid ${activeTab === 'receipt' ? G.accent : 'transparent'}`,
                background: activeTab === 'receipt' ? G.accentBg : 'transparent',
                color: hasReceiptFeature 
                  ? (activeTab === 'receipt' ? G.accent : G.ink)
                  : G.muted,
                cursor: hasReceiptFeature ? "pointer" : "not-allowed",
                fontFamily: "'Sora', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                transition: "all 0.2s",
                marginBottom: -2,
                filter: hasReceiptFeature ? 'none' : 'grayscale(100%)',
                opacity: hasReceiptFeature ? 1 : 0.5
              }}
            >
              🧾 Receipt
            </button>
          </div>

          {/* Helper Text */}
          <div style={{
            padding: "8px 0",
            fontSize: 12,
            color: G.muted,
            fontStyle: "italic"
          }}>
            💡 Tip: Actions below work on the active document. Switch tabs to download/send the other.
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="no-print" style={{ display: "flex", gap: 10, padding: "12px 24px", background: G.surface, borderBottom: `1px solid ${G.border}`, flexWrap: "wrap", alignItems: "center" }}>
        {/* New Invoice - Always available */}
        <Btn 
          onClick={onNewInvoice} 
          style={{ background: G.surface2, color: G.ink, border: `1px solid ${G.border}` }}
        >
          ➕ New Invoice
        </Btn>
        
        {/* Download PDF - Always available */}
        <Btn 
          onClick={() => window.print()} 
          style={{ background: G.accent, color: G.accentFg }}
          title="Press Ctrl+P (or Cmd+P on Mac) and select 'Save as PDF'"
        >
          🖨️ Download PDF
        </Btn>
        
        {/* Email - Exclusive tier only */}
        <Btn
          onClick={handleEmailClick}
          style={getButtonStyle(
            { background: G.surface2, color: G.ink, border: `1px solid ${G.border}` },
            hasEmailFeature
          )}
          title={hasEmailFeature ? "Email invoice to customer" : "Exclusive tier required"}
        >
          📧 Email
        </Btn>
        
        {/* WhatsApp - Exclusive tier only */}
        <Btn 
          onClick={handleWhatsAppClick}
          style={getButtonStyle(
            { background: "#E7F9EE", color: "#1A7A3C", border: "1px solid #A8D9B8", display: "inline-flex", alignItems: "center", gap: 7 },
            hasWhatsAppFeature
          )}
          title={hasWhatsAppFeature ? "Send invoice via WhatsApp" : "Exclusive tier required"}
        >
          <svg width="15" height="15" viewBox="0 0 48 48" fill="none"><path d="M24 4C12.95 4 4 12.95 4 24c0 3.6.96 7.0 2.64 9.95L4 44l10.34-2.6A19.86 19.86 0 0024 44c11.05 0 20-8.95 20-20S35.05 4 24 4z" fill="#25D366"/><path d="M35.2 28.6c-.5-.25-2.97-1.46-3.43-1.63-.46-.17-.8-.25-1.13.25-.34.5-1.3 1.63-1.6 1.97-.29.33-.58.37-1.08.12-.5-.25-2.1-.77-4-2.46-1.48-1.32-2.48-2.95-2.77-3.45-.29-.5-.03-.77.22-1.02.22-.22.5-.58.75-.87.25-.3.33-.5.5-.84.17-.33.08-.62-.04-.87-.12-.25-1.13-2.72-1.55-3.72-.4-.97-.82-.84-1.13-.86-.29-.01-.62-.01-.96-.01-.33 0-.87.12-1.33.62-.46.5-1.75 1.7-1.75 4.16s1.79 4.83 2.04 5.16c.25.33 3.52 5.37 8.53 7.53 1.19.51 2.12.82 2.84 1.05 1.2.38 2.28.33 3.14.2.96-.14 2.97-1.21 3.39-2.38.42-1.17.42-2.17.29-2.38-.12-.2-.46-.33-.96-.58z" fill="#fff"/></svg>
          WhatsApp
        </Btn>
        
        {/* Edit Invoice - Exclusive tier only */}
        <Btn 
          onClick={handleEditClick} 
          style={getButtonStyle(
            { background: G.surface2, color: G.ink, border: `1px solid ${G.border}` },
            hasEditFeature
          )}
          title={hasEditFeature ? "Edit this invoice" : "Exclusive tier required"}
        >
          ✏️ Edit Invoice
        </Btn>
        
        {/* Delete Invoice - Professional+ tier */}
        <Btn 
          onClick={handleDeleteClick} 
          style={getButtonStyle(
            { background: G.dangerBg, color: G.danger, border: `1px solid ${G.danger}` },
            hasDeleteFeature
          )}
          title={hasDeleteFeature ? "Delete this invoice" : "Professional tier or higher required"}
        >
          🗑️ Delete Invoice
        </Btn>
      </div>

      {/* Payment Summary Card - Only for PAID tier with payments */}
      {!isFREE && payments.length > 0 && (
        <div className="no-print" style={{ 
          maxWidth: 1200, 
          margin: "24px auto", 
          padding: "0 24px" 
        }}>
          <div style={{
            background: paymentSummary.status === "paid" 
              ? "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)"
              : paymentSummary.status === "partial"
              ? "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)"
              : "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
            border: `1px solid ${
              paymentSummary.status === "paid" ? "rgba(34, 197, 94, 0.3)" :
              paymentSummary.status === "partial" ? "rgba(234, 179, 8, 0.3)" :
              "rgba(239, 68, 68, 0.3)"
            }`,
            borderRadius: 12,
            padding: "20px 24px",
            marginBottom: 24
          }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "flex-start",
              marginBottom: 16
            }}>
              <div>
                <div style={{ 
                  fontSize: 14, 
                  fontWeight: 700, 
                  color: G.ink,
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}>
                  💰 Payment Summary
                  <span style={{
                    background: paymentSummary.status === "paid" ? "#22C55E" :
                      paymentSummary.status === "partial" ? "#EAB308" : "#EF4444",
                    color: "#FFFFFF",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase"
                  }}>
                    {paymentSummary.status === "paid" ? "✓ Paid" :
                     paymentSummary.status === "partial" ? "⏳ Partial" : "⚠ Unpaid"}
                  </span>
                </div>
              </div>
            </div>

            {/* Summary Grid */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(3, 1fr)", 
              gap: 16,
              marginBottom: 20
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                  Total Amount
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#2B6CB0" }}>
                  {fmtAmt(data.grandTotal)}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                  Amount Paid
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#22C55E" }}>
                  {fmtAmt(paymentSummary.totalPaid)}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                  Outstanding
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#EF4444" }}>
                  {fmtAmt(paymentSummary.outstanding)}
                </div>
              </div>
            </div>

            {/* Payment History */}
            <div>
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: G.muted, 
                textTransform: "uppercase", 
                letterSpacing: 0.5,
                marginBottom: 10
              }}>
                Payment History ({payments.length} transaction{payments.length !== 1 ? "s" : ""})
              </div>
              <div style={{ 
                background: "rgba(255, 255, 255, 0.6)",
                border: `1px solid ${G.border}`,
                borderRadius: 8,
                overflow: "hidden"
              }}>
                {payments.map((payment, i) => (
                  <div 
                    key={payment.id} 
                    style={{ 
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderBottom: i < payments.length - 1 ? `1px solid ${G.border}` : "none",
                      background: i % 2 === 0 ? "transparent" : "rgba(255, 255, 255, 0.3)"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#22C55E"
                      }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: G.ink }}>
                          {new Date(payment.paidAt).toLocaleDateString("en-IN", { 
                            day: "numeric", 
                            month: "short", 
                            year: "numeric" 
                          })}
                        </div>
                        <div style={{ fontSize: 11, color: G.muted }}>
                          {payment.paymentMode}
                          {payment.paymentDetails && ` • ${payment.paymentDetails}`}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#22C55E" }}>
                      {fmtAmt(payment.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice document */}
      {activeTab === 'invoice' && (
        <div className="invoice-page" style={{ ...pageStyleVars, margin: "24px auto", background: "#FFFFFF", borderRadius: 10, boxShadow: "0 4px 32px rgba(0,0,0,.10)", position: "relative", overflow: "hidden", fontFamily: "'Sora', sans-serif", color: "#1C1C1E" }}>
        {/* Watermark */}
        {data.seller?.watermarkType !== "none" && data.seller?.watermarkType && (
          <div className="watermark-container" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 0 }}>
            {data.seller.watermarkType === "text" && data.seller.watermarkText && (
              <div style={{ fontSize: `${40 * scale}px`, fontWeight: 800, color: "rgba(43,108,176,0.15)", textAlign: "center", padding: "0 40px", wordBreak: "break-word", maxWidth: "80%", fontFamily: "'Lora',serif", letterSpacing: 3.5 }}>
                {data.seller.watermarkText}
              </div>
            )}
            {data.seller.watermarkType === "image" && data.seller.watermarkImage && (
              <img
                src={data.seller.watermarkImage}
                alt="watermark"
                style={{ width: "auto", height: "100%", maxWidth: "100%", objectFit: "contain", opacity: 0.15, filter: "grayscale(0%)" }}
              />
            )}
          </div>
        )}

        <div style={{ position: "relative", zIndex: 1, paddingBottom: 25 }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", alignItems: "center", marginBottom: 19 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {data.seller?.logo && (
                <div style={{ width: 97, height: 97, flexShrink: 0 }}>
                  <img src={data.seller.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                </div>
              )}
              <div>
                <div style={{ fontFamily: "'Lora',serif", fontSize: "var(--fs-2xl)", fontWeight: 700, lineHeight: 1.15 }}>{data.seller?.name || ""}</div>
                {data.seller?.tagline && (<div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 5, lineHeight: 1.35 }}>{data.seller.tagline}</div>)}
              </div>
            </div>

            <div style={{ textAlign: "right", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 84 }}>
              <div style={{ fontFamily: "'Lora',serif", fontSize: "var(--fs-xl)", fontWeight: 600, color: "#2B6CB0" }}>{invTitle}</div>
              <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>#{data.invNo}</div>
              <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>
                {(() => {
                  const format = data.formatSettings?.dateFormat || "DD-MM-YYYY";
                  const separator = data.formatSettings?.dateSeparator || "-";
                  const date = new Date(data.invDate);
                  const dd = String(date.getDate()).padStart(2, "0");
                  const mm = String(date.getMonth() + 1).padStart(2, "0");
                  const yyyy = date.getFullYear();
                  switch (format) {
                    case "DD-MM-YYYY": return `${dd}${separator}${mm}${separator}${yyyy}`;
                    case "MM-DD-YYYY": return `${mm}${separator}${dd}${separator}${yyyy}`;
                    case "YYYY-MM-DD": return `${yyyy}${separator}${mm}${separator}${dd}`;
                    default: return `${dd}${separator}${mm}${separator}${yyyy}`;
                  }
                })()}
                {data.timestamp && (<span style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>{" · "}{data.timestamp.split(", ")[1]}</span>)}
              </div>
            </div>
          </div>
          <div style={{ height: 1, background: "#2B6CB0", borderRadius: 2, marginBottom: 16, opacity: .2 }} />

          {/* Billed to / From */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: "#8E8E96", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4.5 }}>Billed To</div>
              <div style={{ fontWeight: 600, fontSize: "var(--fs-md)", marginBottom: 3 }}>{data.customer.name}</div>
             
              {(() => {
                const line1 = data.customer.addressLine1 || 
                  [data.customer.unit, data.customer.building, data.customer.street].filter(Boolean).join(", ");
                
                const line2 = data.customer.addressLine2 || 
                  [data.customer.area, data.customer.landmark].filter(Boolean).join(", ");
                
                const addressParts = [];
                if (line1) addressParts.push(line1);
                if (line2) addressParts.push(line2);
                if (data.customer.city) addressParts.push(data.customer.city);
                if (data.customer.state) addressParts.push(data.customer.state);
               
                const countryPinParts = [];
                if (data.customer.country) countryPinParts.push(data.customer.country);
                if (data.customer.zip) countryPinParts.push(data.customer.zip);
                const countryPin = countryPinParts.join(" - ");
               
                if (!addressParts.length && !countryPin && data.customer.address) {
                  return (
                    <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginBottom: 3, lineHeight: 1.5 }}>
                      {data.customer.address}
                    </div>
                  );
                }
               
                const fullAddress = [];
                if (addressParts.length > 0) fullAddress.push(addressParts.join(", "));
                if (countryPin) fullAddress.push(countryPin);
               
                if (fullAddress.length > 0) {
                  return (
                    <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginBottom: 3, lineHeight: 1.5 }}>
                      {fullAddress.join(", ")}
                    </div>
                  );
                }
               
                return null;
              })()}
             
              {data.customer.phone && <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 2 }}>📞 {data.customer.phone}</div>}
              {data.customer.email && <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>✉ {data.customer.email}</div>}
              {data.customer.gstin && <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 1 }}>Tax ID: {data.customer.gstin}</div>}
            </div>
           
            <div>
              <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: "#8E8E96", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4.5 }}>From</div>
              <div style={{ fontWeight: 600, fontSize: "var(--fs-md)" }}>{data.seller?.name}</div>

              {(() => {
                const line1 = data.seller?.addressLine1 || 
                  [data.seller?.unit, data.seller?.building, data.seller?.street].filter(Boolean).join(", ");
                
                const line2 = data.seller?.addressLine2 || 
                  [data.seller?.area, data.seller?.landmark].filter(Boolean).join(", ");
                
                const addressParts = [];
                if (line1) addressParts.push(line1);
                if (line2) addressParts.push(line2);
                if (data.seller?.city) addressParts.push(data.seller.city);
                if (data.seller?.state) addressParts.push(data.seller.state);
               
                const countryPinParts = [];
                if (data.seller?.country) countryPinParts.push(data.seller.country);
                if (data.seller?.zip) countryPinParts.push(data.seller.zip);
                const countryPin = countryPinParts.join(" - ");
               
                if (!addressParts.length && !countryPin && data.seller?.address) {
                  return (
                    <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 2, lineHeight: 1.5 }}>
                      {data.seller.address}
                    </div>
                  );
                }
               
                const fullAddress = [];
                if (addressParts.length > 0) fullAddress.push(addressParts.join(", "));
                if (countryPin) fullAddress.push(countryPin);
               
                if (fullAddress.length > 0) {
                  return (
                    <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 2, lineHeight: 1.5 }}>
                      {fullAddress.join(", ")}
                    </div>
                  );
                }
               
                return null;
              })()}

              {data.seller?.phone  && <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>📞 {data.seller.phone}</div>}
              {data.seller?.email  && <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>✉ {data.seller.email}</div>}
              {data.seller?.taxId  && <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 1 }}>{getTaxIdLabel(data.seller.taxSystem)}: {data.seller.taxId}</div>}
            </div>
          </div>

          {/* Line items Table */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14, fontSize: "var(--fs-sm)" }}>
            <thead>
              <tr style={{ background: "rgba(28, 28, 30, 0.5)" }}>
                {[
                  ["#", "center"],
                  ...(showPID      ? [["ID",       "left"  ]] : []),
                  ...(showHSN      ? [["HSN",      "left"  ]] : []),
                  ["Product",        "left"],
                  ...(showCF1      ? [[data.formatSettings?.customField1?.name || "CF1", "left"]] : []),
                  ...(showCF2      ? [[data.formatSettings?.customField2?.name || "CF2", "left"]] : []),
                  ["Qty",            "right"],
                  ["Rate",           "right"],
                  ...(showDiscount  ? [["Disc.",    "right" ]] : []),
                  ...(showSGSTCGST  ? [["SGST","right"],["CGST","right"]] : []),
                  ...(showIGST      ? [["IGST",    "right" ]] : []),
                  ...(isCanada      ? canadaTaxCols.map(l => [l, "right"]) : []),
                  ...(isCustom      ? (data.customTaxes||[]).map(t => [t.name, "right"]) : []),
                  ...(!isGSTIndia && !isCanada && !isCustom && data.taxSystem !== "NONE" ? [[data.taxLabel || "Tax", "right"]] : []),
                  ["Amount",         "right"],
                ].map(([h, align]) => (
                  <th key={h} style={{ color: "#F2F2F7", padding: "6.5px 8.5px", textAlign: align, fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: .45 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "rgba(255, 255, 255, 0.5)" : "rgba(247, 246, 243, 0.5)" }}>
                  <td style={{ padding: "7.5px 8.5px", textAlign: "center", color: "#8E8E96" }}>{i + 1}</td>
                  {showPID && <td style={{ padding: "7.5px 8.5px", color: "#636369" }}>{l.productId || "—"}</td>}
                  {showHSN && <td style={{ padding: "7.5px 8.5px", color: "#636369" }}>{l.hsn || "—"}</td>}
                  <td style={{ padding: "7.5px 8.5px", fontWeight: 500 }}>{l.name}</td>
                  {showCF1 && <td style={{ padding: "7.5px 8.5px", color: "#636369", fontSize: "var(--fs-xxs)" }}>{l.customField1Value || "—"}</td>}
                  {showCF2 && <td style={{ padding: "7.5px 8.5px", color: "#636369", fontSize: "var(--fs-xxs)" }}>{l.customField2Value || "—"}</td>}
                  
                  <td style={{ padding: "7.5px 8.5px", textAlign: "right" }}>{l.qty}</td>
                  <td style={{ padding: "7.5px 8.5px", textAlign: "right" }}>{fmtAmt(l.price)}</td>
                  {showDiscount && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#276749" }}>{l.discAmt > 0 ? `−${fmtAmt(l.discAmt)}` : "—"}</td>}
                  {showSGSTCGST && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{l.split.sgst}%/{fmtAmt(l.sgstAmt)}</td>}
                  {showSGSTCGST && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{l.split.cgst}%/{fmtAmt(l.cgstAmt)}</td>}
                  {showIGST     && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{l.split.igst}%/{fmtAmt(l.igstAmt)}</td>}
                  {isCanada     && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{l.tax}%/{fmtAmt(l.otherAmt)}</td>}
                  {isCanada && canadaTaxCols.length > 1 && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{l.tax2}%/{fmtAmt(l.other2Amt)}</td>}
                  {isCustom && (data.customTaxes||[]).map((t, ti) => <td key={ti} style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{t.rate}%/{fmtAmt(l.afterDisc * parseFloat(t.rate||0) / 100)}</td>)}
                  {!isGSTIndia && !isCanada && !isCustom && data.taxSystem !== "NONE" && <td style={{ padding: "7.5px 8.5px", textAlign: "right", color: "#636369" }}>{l.tax}%/{fmtAmt(l.otherAmt)}</td>}
                  <td style={{ padding: "7.5px 8.5px", textAlign: "right", fontWeight: 600 }}>{fmtAmt(l.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #1C1C1E", background: "rgba(247, 246, 243, 0.4)" }}>
                <td colSpan={2 + (showPID?1:0) + (showHSN?1:0) + (showCF1?1:0) + (showCF2?1:0)} style={{ padding: "7.5px 8.5px", fontWeight: 600, fontSize: "var(--fs-sm)" }}>
                  {data.totalItems} item{data.totalItems !== 1 ? "s" : ""} · {data.totalQty} qty
                </td>
                <td style={{ padding: "7.5px 8.5px", textAlign: "right", fontWeight: 600 }}>{data.totalQty}</td>
              </tr>
            </tfoot>
          </table>

          {/* Totals summary */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
            <div style={{ minWidth: 240 }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#636369", fontSize: "var(--fs-sm2)" }}><span>Subtotal</span><span>{fmtAmt(data.subtotal)}</span></div>
              {data.itemDiscountTotal > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#276749", fontSize: "var(--fs-sm2)" }}><span>Item Discounts</span><span>−{fmtAmt(data.itemDiscountTotal)}</span></div>}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#636369", fontSize: "var(--fs-sm2)" }}><span>Tax</span><span>{fmtAmt(data.totalTax)}</span></div>
              {data.overallDiscAmt > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#276749", fontSize: "var(--fs-sm2)" }}><span>Overall Discount</span><span>−{fmtAmt(data.overallDiscAmt)}</span></div>}
              <div style={{ height: 1.5, background: "#1C1C1E", margin: "5.5px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "var(--fs-lg)" }}><span>Total</span><span style={{ color: "#2B6CB0" }}>{fmtAmt(data.grandTotal)}</span></div>
            </div>
          </div>

          {/* Signature */}
          {data.seller?.signature && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
              <div style={{ textAlign: "center" }}>
                <img src={data.seller.signature} alt="sig" style={{ height: 60, objectFit: "contain", marginBottom: 2.5 }} />
                <div style={{ borderTop: "1px solid #E2DED6", paddingTop: 2.5, fontSize: "var(--fs-sm2)", color: "#8E8E96" }}>Authorised Signatory</div>
                {(data.seller?.owner || data.seller?.name) && (
                  <div style={{ fontSize: "var(--fs-md2)", fontWeight: 500 }}>{data.seller.owner || data.seller.name}</div>
                )}
              </div>
            </div>
          )}

          {/* Notes / Terms */}
          {(data.terms || []).filter(s => s.title || s.body).length > 0 && (
            <div style={{ borderTop: "1px solid #E2DED6", paddingTop: 11, marginTop: 3.5 }}>
              {(data.terms || []).filter(s => s.title || s.body).map((s, i) => (
                <div key={i} style={{ marginBottom: 7.5 }}>
                  {s.title && <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: 2 }}>{s.title}</div>}
                  {s.body  && <div style={{ fontSize: "var(--fs-sm)", color: "#636369", whiteSpace: "pre-line" }}>{s.body}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="invoice-footer" style={{ marginTop: 0, paddingTop: 11, borderTop: "1px solid #E2DED6", textAlign: "center", color: "#8E8E96", fontSize: "var(--fs-sm)" }}>
          Thank you for your business!{(data.seller?.email || data.seller?.phone) && ` · ${data.seller.email || data.seller.phone}`}
        </div>
        </div>
      )}

      {/* Receipt document */}
      {activeTab === 'receipt' && (
        <div className="receipt-page" style={{ ...pageStyleVars, margin: "24px auto", background: "#FFFFFF", borderRadius: 10, boxShadow: "0 4px 32px rgba(0,0,0,.10)", position: "relative", overflow: "hidden", fontFamily: "'Sora', sans-serif", color: "#1C1C1E" }}>

          {/* Watermark */}
          {data.seller?.watermarkType !== "none" && data.seller?.watermarkType && (
            <div className="watermark-container" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 0 }}>
              {data.seller.watermarkType === "text" && data.seller.watermarkText && (
                <div style={{ fontSize: `${40 * scale}px`, fontWeight: 800, color: "rgba(43,108,176,0.15)", textAlign: "center", padding: "0 40px", wordBreak: "break-word", maxWidth: "80%", fontFamily: "'Lora',serif", letterSpacing: 3.5 }}>
                  {data.seller.watermarkText}
                </div>
              )}
              {data.seller.watermarkType === "image" && data.seller.watermarkImage && (
                <img
                  src={data.seller.watermarkImage}
                  alt="watermark"
                  style={{ width: "auto", height: "100%", maxWidth: "100%", objectFit: "contain", opacity: 0.15, filter: "grayscale(0%)" }}
                />
              )}
            </div>
          )}

          <div style={{ position: "relative", zIndex: 1, paddingBottom: 25 }}>
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", alignItems: "center", marginBottom: 19 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {data.seller?.logo && (
                  <div style={{ width: 97, height: 97, flexShrink: 0 }}>
                    <img src={data.seller.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                  </div>
                )}
                <div>
                  <div style={{ fontFamily: "'Lora',serif", fontSize: "var(--fs-2xl)", fontWeight: 700, lineHeight: 1.15 }}>{data.seller?.name || ""}</div>
                  {data.seller?.tagline && (<div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 5, lineHeight: 1.35 }}>{data.seller.tagline}</div>)}
                </div>
              </div>

              <div style={{ textAlign: "right", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 84 }}>
                <div style={{ fontFamily: "'Lora',serif", fontSize: "var(--fs-xl)", fontWeight: 600, color: "#2B6CB0" }}>PAYMENT RECEIPT</div>
                <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>#{data.invNo}</div>
                <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>
                  {(() => {
                    const format = data.formatSettings?.dateFormat || "DD-MM-YYYY";
                    const separator = data.formatSettings?.dateSeparator || "-";
                    const date = new Date();
                    const dd = String(date.getDate()).padStart(2, "0");
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const yyyy = date.getFullYear();
                    switch (format) {
                      case "DD-MM-YYYY": return `${dd}${separator}${mm}${separator}${yyyy}`;
                      case "MM-DD-YYYY": return `${mm}${separator}${dd}${separator}${yyyy}`;
                      case "YYYY-MM-DD": return `${yyyy}${separator}${mm}${separator}${dd}`;
                      default: return `${dd}${separator}${mm}${separator}${yyyy}`;
                    }
                  })()}
                </div>
              </div>
            </div>
            <div style={{ height: 1, background: "#2B6CB0", borderRadius: 2, marginBottom: 16, opacity: .2 }} />

            {/* Received From / From */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: "#8E8E96", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4.5 }}>Received From</div>
                <div style={{ fontWeight: 600, fontSize: "var(--fs-md)", marginBottom: 3 }}>{data.customer.name}</div>
                
                {(() => {
                  const line1 = data.customer.addressLine1 || 
                    [data.customer.unit, data.customer.building, data.customer.street].filter(Boolean).join(", ");
                  
                  const line2 = data.customer.addressLine2 || 
                    [data.customer.area, data.customer.landmark].filter(Boolean).join(", ");
                  
                  const addressParts = [];
                  if (line1) addressParts.push(line1);
                  if (line2) addressParts.push(line2);
                  if (data.customer.city) addressParts.push(data.customer.city);
                  if (data.customer.state) addressParts.push(data.customer.state);
                 
                  const countryPinParts = [];
                  if (data.customer.country) countryPinParts.push(data.customer.country);
                  if (data.customer.zip) countryPinParts.push(data.customer.zip);
                  const countryPin = countryPinParts.join(" - ");
                 
                  if (!addressParts.length && !countryPin && data.customer.address) {
                    return (
                      <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginBottom: 3, lineHeight: 1.5 }}>
                        {data.customer.address}
                      </div>
                    );
                  }
                 
                  const fullAddress = [];
                  if (addressParts.length > 0) fullAddress.push(addressParts.join(", "));
                  if (countryPin) fullAddress.push(countryPin);
                 
                  if (fullAddress.length > 0) {
                    return (
                      <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginBottom: 3, lineHeight: 1.5 }}>
                        {fullAddress.join(", ")}
                      </div>
                    );
                  }
                 
                  return null;
                })()}
               
                {data.customer.phone && <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 2 }}>📞 {data.customer.phone}</div>}
                {data.customer.email && <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>✉ {data.customer.email}</div>}
                {data.customer.gstin && <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 1 }}>Tax ID: {data.customer.gstin}</div>}
              </div>
             
              <div>
                <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: "#8E8E96", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4.5 }}>From</div>
                <div style={{ fontWeight: 600, fontSize: "var(--fs-md)" }}>{data.seller?.name}</div>

                {(() => {
                  const line1 = data.seller?.addressLine1 || 
                    [data.seller?.unit, data.seller?.building, data.seller?.street].filter(Boolean).join(", ");
                  
                  const line2 = data.seller?.addressLine2 || 
                    [data.seller?.area, data.seller?.landmark].filter(Boolean).join(", ");
                  
                  const addressParts = [];
                  if (line1) addressParts.push(line1);
                  if (line2) addressParts.push(line2);
                  if (data.seller?.city) addressParts.push(data.seller.city);
                  if (data.seller?.state) addressParts.push(data.seller.state);
                 
                  const countryPinParts = [];
                  if (data.seller?.country) countryPinParts.push(data.seller.country);
                  if (data.seller?.zip) countryPinParts.push(data.seller.zip);
                  const countryPin = countryPinParts.join(" - ");
                 
                  if (!addressParts.length && !countryPin && data.seller?.address) {
                    return (
                      <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 2, lineHeight: 1.5 }}>
                        {data.seller.address}
                      </div>
                    );
                  }
                 
                  const fullAddress = [];
                  if (addressParts.length > 0) fullAddress.push(addressParts.join(", "));
                  if (countryPin) fullAddress.push(countryPin);
                 
                  if (fullAddress.length > 0) {
                    return (
                      <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 2, lineHeight: 1.5 }}>
                        {fullAddress.join(", ")}
                      </div>
                    );
                  }
                 
                  return null;
                })()}

                {data.seller?.phone  && <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>📞 {data.seller.phone}</div>}
                {data.seller?.email  && <div style={{ color: "#636369", fontSize: "var(--fs-sm)" }}>✉ {data.seller.email}</div>}
                {data.seller?.taxId  && <div style={{ color: "#636369", fontSize: "var(--fs-sm)", marginTop: 1 }}>{getTaxIdLabel(data.seller.taxSystem)}: {data.seller.taxId}</div>}
              </div>
            </div>

            {/* Payment Details Table */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14, fontSize: "var(--fs-sm)" }}>
              <thead>
                <tr style={{ background: "rgba(28, 28, 30, 0.5)" }}>
                  <th style={{ color: "#F2F2F7", padding: "6.5px 8.5px", textAlign: "center", fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: .45 }}>#</th>
                  <th style={{ color: "#F2F2F7", padding: "6.5px 8.5px", textAlign: "left", fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: .45 }}>Payment Date</th>
                  <th style={{ color: "#F2F2F7", padding: "6.5px 8.5px", textAlign: "left", fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: .45 }}>Mode</th>
                  <th style={{ color: "#F2F2F7", padding: "6.5px 8.5px", textAlign: "left", fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: .45 }}>Details</th>
                  <th style={{ color: "#F2F2F7", padding: "6.5px 8.5px", textAlign: "right", fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: .45 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: "40px 20px", textAlign: "center", color: G.muted, fontSize: "var(--fs-md)", fontStyle: "italic" }}>
                      No payments received yet
                    </td>
                  </tr>
                ) : (
                  payments.map((payment, i) => (
                    <tr key={payment.id} style={{ background: i % 2 === 0 ? "rgba(255, 255, 255, 0.5)" : "rgba(247, 246, 243, 0.5)" }}>
                      <td style={{ padding: "7.5px 8.5px", textAlign: "center", color: "#8E8E96" }}>{i + 1}</td>
                      <td style={{ padding: "7.5px 8.5px", fontWeight: 500 }}>
                        {new Date(payment.paidAt).toLocaleDateString("en-IN", { 
                          day: "numeric", 
                          month: "short", 
                          year: "numeric" 
                        })}
                      </td>
                      <td style={{ padding: "7.5px 8.5px", color: "#636369" }}>{payment.paymentMode || "—"}</td>
                      <td style={{ padding: "7.5px 8.5px", color: "#636369" }}>{payment.paymentDetails || "—"}</td>
                      <td style={{ padding: "7.5px 8.5px", textAlign: "right", fontWeight: 600, color: "#22C55E" }}>{fmtAmt(payment.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Payment Summary */}
            <div style={{ borderTop: "1px solid #E2DED6", paddingTop: 14, marginBottom: 18 }}>
              <div style={{ fontSize: "var(--fs-sm)", color: "#636369", marginBottom: 12, fontStyle: "italic" }}>
                Payments received against Invoice #{data.invNo}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ minWidth: 280 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#636369", fontSize: "var(--fs-sm2)" }}>
                    <span>Invoice Total</span>
                    <span>{fmtAmt(data.grandTotal)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#22C55E", fontSize: "var(--fs-sm2)", fontWeight: 600 }}>
                    <span>Total Paid</span>
                    <span>{fmtAmt(paymentSummary?.totalPaid || 0)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2.5px 0", color: "#EF4444", fontSize: "var(--fs-sm2)", fontWeight: 600 }}>
                    <span>Outstanding</span>
                    <span>{fmtAmt(paymentSummary?.outstanding || data.grandTotal)}</span>
                  </div>
                  <div style={{ height: 1.5, background: "#1C1C1E", margin: "5.5px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "var(--fs-lg)" }}>
                    <span>Payment Status</span>
                    <span style={{ 
                      background: paymentSummary?.status === "paid" ? "#22C55E" :
                        paymentSummary?.status === "partial" ? "#EAB308" : "#EF4444",
                      color: "#FFFFFF",
                      padding: "4px 12px",
                      borderRadius: 6,
                      fontSize: "var(--fs-sm)",
                      fontWeight: 600,
                      textTransform: "uppercase"
                    }}>
                      {paymentSummary?.status === "paid" ? "✓ Paid" :
                       paymentSummary?.status === "partial" ? "⏳ Partial" : 
                       payments.length === 0 ? "⚠ Unpaid" : "⚠ Unpaid"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Signature */}
            {data.seller?.signature && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
                <div style={{ textAlign: "center" }}>
                  <img src={data.seller.signature} alt="sig" style={{ height: 60, objectFit: "contain", marginBottom: 2.5 }} />
                  <div style={{ borderTop: "1px solid #E2DED6", paddingTop: 2.5, fontSize: "var(--fs-sm2)", color: "#8E8E96" }}>Authorised Signatory</div>
                  {(data.seller?.owner || data.seller?.name) && (
                    <div style={{ fontSize: "var(--fs-md2)", fontWeight: 500 }}>{data.seller.owner || data.seller.name}</div>
                  )}
                </div>
              </div>
            )}

            {/* Notes / Terms (if any) */}
            {(data.terms || []).filter(s => s.title || s.body).length > 0 && (
              <div style={{ borderTop: "1px solid #E2DED6", paddingTop: 11, marginTop: 3.5 }}>
                {(data.terms || []).filter(s => s.title || s.body).map((s, i) => (
                  <div key={i} style={{ marginBottom: 7.5 }}>
                    {s.title && <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: 2 }}>{s.title}</div>}
                    {s.body  && <div style={{ fontSize: "var(--fs-sm)", color: "#636369", whiteSpace: "pre-line" }}>{s.body}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="receipt-footer" style={{ marginTop: 0, paddingTop: 11, borderTop: "1px solid #E2DED6", textAlign: "center", color: "#8E8E96", fontSize: "var(--fs-sm)" }}>
            Thank you for your business!{(data.seller?.email || data.seller?.phone) && ` · ${data.seller.email || data.seller.phone}`}
          </div>
        </div>
      )}
    </div>
  );
}